/**
 * Frequently Bought Together Bundle
 * Applies discount code automatically in cart
 * Default/exclude driven by variant metafields rendered into JSON:
 * - bundle_default (boolean)
 * - bundle_exclude (boolean)
 */

(function() {
  'use strict';

  class FBTBundle {
    constructor(container) {
      this.container = container;
      this.config = this.parseConfig();

      if (!this.config) {
        console.error('FBT Bundle: Configuration not found');
        return;
      }

      // Resolve i18n strings injected by the Liquid section, keyed by section id.
      // Falls back to an empty object so German JS fallbacks below still apply.
      this.i18n = (window.MF_FBT_I18N && window.MF_FBT_I18N[this.config.sectionId]) || {};

      this.bundleId = `bundle_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      this.state = {
        productA: { selectedOptions: {}, variantId: null },
        productB: { selectedOptions: {}, variantId: null },
        isLoading: false
      };

      this.elements = {
        addToCartBtn: container.querySelector('[data-add-to-cart]'),
        buttonText: container.querySelector('[data-button-text]'),
        loading: container.querySelector('[data-loading]'),
        errorMessage: container.querySelector('[data-error-message]'),
        pricing: container.querySelector('[data-pricing]'),
        comparePrice: container.querySelector('[data-compare-price]'),
        finalPrice: container.querySelector('[data-final-price]'),
        savings: container.querySelector('[data-savings]'),
        savingsAmount: container.querySelector('[data-savings-amount]'),
        productAOptions: container.querySelector('[data-product-options="a"]'),
        productBOptions: container.querySelector('[data-product-options="b"]'),
        productAPrice: container.querySelector('[data-product-price="a"]'),
        productBPrice: container.querySelector('[data-product-price="b"]'),
        productAImage: container.querySelector('[data-product-image="a"]'),
        productBImage: container.querySelector('[data-product-image="b"]')
      };

      this.init();
    }

    parseConfig() {
      const configEl = this.container.querySelector('[data-fbt-config]');
      if (!configEl) return null;

      try {
        return JSON.parse(configEl.textContent);
      } catch (e) {
        console.error('FBT Bundle: Failed to parse config', e);
        return null;
      }
    }

    init() {
      // Render UI
      this.renderProductOptions('productA', this.config.productA);
      this.renderProductOptions('productB', this.config.productB);

      // Apply defaults AFTER UI exists
      this.applyDefaultSelection('productA', this.config.productA);
      this.applyDefaultSelection('productB', this.config.productB);

      if (this.elements.addToCartBtn) {
        this.elements.addToCartBtn.addEventListener('click', () => this.addToCart());
      }

      this.updateUI();
    }

    // ------------------------------
    // Helpers: real options detection
    // ------------------------------
    hasRealOptions(productData) {
      if (!productData || !Array.isArray(productData.options) || productData.options.length === 0) return false;

      if (
        productData.options.length === 1 &&
        String(productData.options[0].name || '').toLowerCase() === 'title' &&
        Array.isArray(productData.options[0].values) &&
        productData.options[0].values.length === 1
      ) {
        return false;
      }

      return productData.options.some(opt => Array.isArray(opt.values) && opt.values.length > 1);
    }

    // ------------------------------
    // Default / exclude logic
    // ------------------------------
    isExcluded(variant) {
      return !!(variant && variant.bundle_exclude);
    }

    isMarkedDefault(variant) {
      return !!(variant && variant.bundle_default);
    }

    pickDefaultVariant(productData) {
      if (!productData || !Array.isArray(productData.variants) || productData.variants.length === 0) return null;

      const notExcluded = productData.variants.filter(v => !this.isExcluded(v));
      if (notExcluded.length === 0) return null;

      const marked = notExcluded.filter(v => this.isMarkedDefault(v));
      if (marked.length === 1) {
        return marked[0];
      }
      if (marked.length > 1) {
        const markedAvail = marked.find(v => v.available);
        return markedAvail || marked[0];
      }

      const avail = notExcluded.find(v => v.available);
      return avail || notExcluded[0];
    }

    applyDefaultSelection(productKey, productData) {
      if (!productData) return;

      const optionsContainer = productKey === 'productA' ? this.elements.productAOptions : this.elements.productBOptions;

      // If no real options → just select best variant and hide UI
      if (!this.hasRealOptions(productData)) {
        if (optionsContainer) {
          optionsContainer.innerHTML = '';
          optionsContainer.style.display = 'none';
        }

        const v = this.pickDefaultVariant(productData) || productData.variants.find(vr => vr.available) || productData.variants[0];
        if (v) {
          this.state[productKey].variantId = v.id;
          this.state[productKey].selectedOptions = {};
          if (v.featured_image) {
            const imageEl = productKey === 'productA' ? this.elements.productAImage : this.elements.productBImage;
            if (imageEl) imageEl.src = v.featured_image;
          }
        }
        return;
      }

      if (optionsContainer) optionsContainer.style.display = '';

      const v = this.pickDefaultVariant(productData);
      if (!v) return;

      // Set selectedOptions by option index (0-based)
      const selected = {};
      for (let i = 0; i < productData.options.length; i++) {
        const val = v[`option${i + 1}`];
        if (val) selected[i] = val;
      }
      this.state[productKey].selectedOptions = selected;

      // Rebuild selects based on this preselection, then set values
      this.refreshAllSelects(productKey, productData);

      // Resolve variant and update image
      this.resolveVariant(productKey, productData);
      if (v.featured_image) {
        const imageEl = productKey === 'productA' ? this.elements.productAImage : this.elements.productBImage;
        if (imageEl) imageEl.src = v.featured_image;
      }
    }

    // ------------------------------
    // Option ordering (kept)
    // ------------------------------
    getOptionOrder(productData) {
      const colorKeywords = ['farbe', 'color', 'edition', 'colour', 'couleur'];
      const sizeKeywords = ['größe', 'size', 'menge', 'quantity', 'taille'];

      const optionsWithIndex = productData.options.map((option, index) => ({
        ...option,
        originalIndex: index
      }));

      optionsWithIndex.sort((a, b) => {
        const aIsColor = colorKeywords.some(kw => a.name.toLowerCase().includes(kw));
        const bIsColor = colorKeywords.some(kw => b.name.toLowerCase().includes(kw));
        const aIsSize = sizeKeywords.some(kw => a.name.toLowerCase().includes(kw));
        const bIsSize = sizeKeywords.some(kw => b.name.toLowerCase().includes(kw));

        if (aIsColor && !bIsColor) return -1;
        if (!aIsColor && bIsColor) return 1;
        if (aIsSize && !bIsSize) return 1;
        if (!aIsSize && bIsSize) return -1;

        return a.position - b.position;
      });

      return optionsWithIndex;
    }

    renderProductOptions(productKey, productData) {
      const container = productKey === 'productA' ? this.elements.productAOptions : this.elements.productBOptions;
      if (!container || !productData || !productData.options || productData.options.length === 0) return;

      if (!this.hasRealOptions(productData)) {
        container.innerHTML = '';
        container.style.display = 'none';
        return;
      }

      container.style.display = '';
      container.innerHTML = '';

      const orderedOptions = this.getOptionOrder(productData);

      orderedOptions.forEach((option, displayIndex) => {
        const optionDiv = document.createElement('div');
        optionDiv.className = 'fbt-bundle__option';
        optionDiv.dataset.optionIndex = option.originalIndex;
        optionDiv.dataset.displayIndex = displayIndex;

        const label = document.createElement('label');
        label.className = 'fbt-bundle__option-label';
        label.textContent = option.name;
        label.htmlFor = `fbt-${productKey}-option-${displayIndex}`;

        const selectWrapper = document.createElement('div');
        selectWrapper.className = 'select-wrapper';

        const select = document.createElement('select');
        select.id = `fbt-${productKey}-option-${displayIndex}`;
        select.className = 'fbt-bundle__select';
        select.dataset.product = productKey;
        select.dataset.optionIndex = option.originalIndex;
        select.dataset.displayIndex = displayIndex;
        select.dataset.optionName = option.name;

        select.addEventListener('change', (e) => {
          this.handleOptionChange(productKey, productData, option.originalIndex, displayIndex, e.target.value);
        });

        const chevron = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        chevron.setAttribute('class', 'icon icon-chevron-down');
        chevron.setAttribute('width', '10');
        chevron.setAttribute('height', '6');
        chevron.setAttribute('viewBox', '0 0 10 6');
        chevron.setAttribute('fill', 'none');
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', 'M1 1L5 5L9 1');
        path.setAttribute('stroke', 'currentColor');
        path.setAttribute('stroke-width', '1.5');
        path.setAttribute('stroke-linecap', 'round');
        path.setAttribute('stroke-linejoin', 'round');
        chevron.appendChild(path);

        selectWrapper.appendChild(select);
        selectWrapper.appendChild(chevron);

        optionDiv.appendChild(label);
        optionDiv.appendChild(selectWrapper);
        container.appendChild(optionDiv);
      });

      // initial population based on empty selection OR preselection
      this.refreshAllSelects(productKey, productData);
    }

    refreshAllSelects(productKey, productData) {
      const container = productKey === 'productA' ? this.elements.productAOptions : this.elements.productBOptions;
      if (!container) return;

      const orderedOptions = this.getOptionOrder(productData);
      const allSelects = Array.from(container.querySelectorAll('select'));

      orderedOptions.forEach((opt, displayIndex) => {
        const select = allSelects.find(s => parseInt(s.dataset.displayIndex, 10) === displayIndex);
        if (!select) return;

        const optionIndex = opt.originalIndex;

        const currentSelected = this.state[productKey].selectedOptions[optionIndex] || '';

        // rebuild options list
        select.innerHTML = '';
        const defaultOption = document.createElement('option');
        defaultOption.value = '';
        defaultOption.textContent = (this.i18n.optionChoose || '__NAME__ wählen').replace('__NAME__', select.dataset.optionName);
        select.appendChild(defaultOption);

        const availableValues = this.getAvailableValuesForOption(productKey, productData, optionIndex);
        // AUDIT Paket A (28.09.2026, INFO-06): Mengen-Werte mit Einheit anzeigen ("50 Filter"), Wert bleibt roh.
        const mfEinheit = (productData.unit || '').split(' à ')[0].trim(); // "Heftchen à 32 Blättchen" -> "Heftchen" (Select bleibt lesbar)
        const mfIstMenge = String(select.dataset.optionName || '').trim().toLowerCase() === 'menge';
        availableValues.forEach(val => {
          const o = document.createElement('option');
          o.value = val;
          o.textContent = (mfIstMenge && mfEinheit && /^\d+$/.test(String(val).trim())) ? (val + ' ' + mfEinheit) : val;
          select.appendChild(o);
        });

        // enable/disable cascade
        if (displayIndex === 0) {
          select.disabled = false;
        } else {
          const prev = allSelects.find(s => parseInt(s.dataset.displayIndex, 10) === displayIndex - 1);
          select.disabled = !(prev && prev.value) || availableValues.length === 0;
        }

        // re-apply selected value if still available
        if (currentSelected && availableValues.includes(currentSelected)) {
          select.value = currentSelected;
        } else {
          // if invalid, clear it in state
          if (this.state[productKey].selectedOptions[optionIndex]) {
            delete this.state[productKey].selectedOptions[optionIndex];
          }
          select.value = '';
        }
      });
    }

    // IMPORTANT: exclude variants are filtered here
    getAvailableValuesForOption(productKey, productData, optionIndex) {
      const selectedOptions = this.state[productKey].selectedOptions;
      const availableValues = new Set();

      productData.variants.forEach(variant => {
        if (!variant.available) return;
        if (this.isExcluded(variant)) return;

        let matches = true;

        for (let i = 0; i < productData.options.length; i++) {
          if (i === optionIndex) continue;

          const selectedValue = selectedOptions[i];
          const variantValue = variant[`option${i + 1}`];

          if (selectedValue && variantValue !== selectedValue) {
            matches = false;
            break;
          }
        }

        if (matches) {
          const value = variant[`option${optionIndex + 1}`];
          if (value) availableValues.add(value);
        }
      });

      return Array.from(availableValues);
    }

    handleOptionChange(productKey, productData, optionIndex, displayIndex, value) {
      if (value) {
        this.state[productKey].selectedOptions[optionIndex] = value;
      } else {
        delete this.state[productKey].selectedOptions[optionIndex];
      }

      // refresh everything (simple + robust)
      this.refreshAllSelects(productKey, productData);

      this.resolveVariant(productKey, productData);
      this.updateUI();
    }

    resolveVariant(productKey, productData) {
      const selectedOptions = this.state[productKey].selectedOptions;

      if (!this.hasRealOptions(productData)) {
        const v = this.pickDefaultVariant(productData);
        this.state[productKey].variantId = v ? v.id : null;
        return;
      }

      const allSelected = productData.options.every((_, index) => selectedOptions[index]);
      if (!allSelected) {
        this.state[productKey].variantId = null;
        return;
      }

      const matchingVariant = productData.variants.find(variant => {
        if (this.isExcluded(variant)) return false;
        return productData.options.every((_, index) => variant[`option${index + 1}`] === selectedOptions[index]);
      });

      this.state[productKey].variantId = matchingVariant ? matchingVariant.id : null;

      if (matchingVariant && matchingVariant.featured_image) {
        const imageEl = productKey === 'productA' ? this.elements.productAImage : this.elements.productBImage;
        if (imageEl) imageEl.src = matchingVariant.featured_image;
      }
    }

    getVariant(productKey) {
      const variantId = this.state[productKey].variantId;
      if (!variantId) return null;

      const productData = productKey === 'productA' ? this.config.productA : this.config.productB;
      return productData.variants.find(v => v.id === variantId);
    }

    calculatePricing() {
      const variantA = this.getVariant('productA');
      const variantB = this.getVariant('productB');

      if (!variantA || !variantB) return null;

      const quantity = this.config.defaultQuantity || 1;

      const priceA = variantA.price * quantity;
      const priceB = variantB.price * quantity;
      const subtotal = priceA + priceB;

      let discountAmount = 0;
      let finalPrice = subtotal;

      if (this.config.discountValue && this.config.discountValue > 0) {
        if (this.config.discountType === 'percentage') {
          discountAmount = Math.round(subtotal * (this.config.discountValue / 100));
        } else if (this.config.discountType === 'fixed') {
          discountAmount = this.config.discountValue;
        }
        finalPrice = Math.max(0, subtotal - discountAmount);
      }

      return { subtotal, discountAmount, finalPrice };
    }

    formatMoney(cents) {
      const amount = cents / 100;
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
    }

    updateUI() {
      const variantA = this.getVariant('productA');
      const variantB = this.getVariant('productB');

      if (variantA && this.elements.productAPrice) this.elements.productAPrice.textContent = this.formatMoney(variantA.price);
      if (variantB && this.elements.productBPrice) this.elements.productBPrice.textContent = this.formatMoney(variantB.price);

      const canAddToCart = variantA && variantA.available && variantB && variantB.available;

      if (this.elements.addToCartBtn) {
        this.elements.addToCartBtn.disabled = !canAddToCart || this.state.isLoading;

        if (this.elements.buttonText) {
          if (this.state.isLoading) this.elements.buttonText.textContent = this.i18n.adding || 'Wird hinzugefügt...';
          else if (canAddToCart) this.elements.buttonText.textContent = this.i18n.addToCart || 'Bundle in den Warenkorb';
          else this.elements.buttonText.textContent = this.i18n.chooseOptions || 'Bitte Optionen wählen';
        }
      }

      const pricing = this.calculatePricing();

      if (pricing && this.elements.finalPrice) {
        this.elements.finalPrice.textContent = this.formatMoney(pricing.finalPrice);

        if (pricing.discountAmount > 0) {
          if (this.elements.comparePrice) {
            this.elements.comparePrice.textContent = this.formatMoney(pricing.subtotal);
            this.elements.comparePrice.style.display = 'inline';
          }
          if (this.elements.savings && this.elements.savingsAmount) {
            this.elements.savingsAmount.textContent = this.formatMoney(pricing.discountAmount);
            this.elements.savings.style.display = 'block';
          }
        } else {
          if (this.elements.comparePrice) this.elements.comparePrice.style.display = 'none';
          if (this.elements.savings) this.elements.savings.style.display = 'none';
        }
      } else {
        if (this.elements.finalPrice) this.elements.finalPrice.textContent = '—';
        if (this.elements.comparePrice) this.elements.comparePrice.style.display = 'none';
        if (this.elements.savings) this.elements.savings.style.display = 'none';
      }
    }

    async addToCart() {
      if (this.state.isLoading) return;

      const variantA = this.getVariant('productA');
      const variantB = this.getVariant('productB');

      if (!variantA || !variantB) {
        this.showError(this.i18n.chooseAllOptions || 'Bitte alle Optionen auswählen');
        return;
      }

      this.state.isLoading = true;
      this.updateUI();
      this.hideError();

      if (this.elements.loading) this.elements.loading.style.display = 'inline-block';

      const quantity = this.config.defaultQuantity || 1;

      try {
        const items = [
          {
            id: variantA.id,
            quantity,
            properties: {
              '_bundle_id': this.bundleId,
              '_bundle_note': this.config.bundleNote || 'Bundle-Angebot',
              '_bundle_product': 'A'
            }
          },
          {
            id: variantB.id,
            quantity,
            properties: {
              '_bundle_id': this.bundleId,
              '_bundle_note': this.config.bundleNote || 'Bundle-Angebot',
              '_bundle_product': 'B'
            }
          }
        ];

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.description || 'Failed to add to cart');
        }

        await response.json();

        if (this.config.discountCode && this.config.discountCode.trim() !== '') {
          await this.applyDiscountCode(this.config.discountCode.trim());
        }

        this.openCartDrawer();
      } catch (error) {
        console.error('FBT Bundle: Add to cart error', error);
        this.showError(error.message || this.i18n.addError || 'Fehler beim Hinzufügen.');
      } finally {
        this.state.isLoading = false;
        if (this.elements.loading) this.elements.loading.style.display = 'none';
        this.updateUI();
      }
    }

    async applyDiscountCode(code) {
      try {
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ attributes: { 'discount_code': code } })
        });

        if (response.ok) console.log('Discount code applied:', code);

        sessionStorage.setItem('fbt_discount_code', code);
      } catch (error) {
        console.warn('Could not apply discount code automatically:', error);
      }
    }

    openCartDrawer() {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

      setTimeout(() => {
        const cartTrigger =
          document.querySelector('[data-action="open-drawer"][data-drawer-id="cart-drawer"]') ||
          document.querySelector('.header__cart-icon') ||
          document.querySelector('[aria-controls="cart-drawer"]') ||
          document.querySelector('[href="/cart"]');

        if (cartTrigger) {
          if (typeof cartTrigger.click === 'function') cartTrigger.click();
          else cartTrigger.dispatchEvent(new Event('click', { bubbles: true }));
        }
      }, 200);

      window.dispatchEvent(new CustomEvent('theme:cart:open'));
      window.dispatchEvent(new CustomEvent('cart:open'));
    }

    showError(message) {
      if (this.elements.errorMessage) {
        this.elements.errorMessage.textContent = message;
        this.elements.errorMessage.style.display = 'block';
        setTimeout(() => this.hideError(), 5000);
      }
    }

    hideError() {
      if (this.elements.errorMessage) this.elements.errorMessage.style.display = 'none';
    }
  }

  function initFBTBundles() {
    const containers = document.querySelectorAll('[data-section-type="fbt-bundle"]');
    containers.forEach(container => new FBTBundle(container));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFBTBundles);
  } else {
    initFBTBundles();
  }

  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('[data-section-type="fbt-bundle"]')) {
      initFBTBundles();
    }
  });

})();